import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router';
import { getAuth, onAuthStateChanged } from 'firebase/auth';
import { getPersonById } from '../data/mockPeople';
import ProfileHeader from '../components/profile/ProfileHeader';
import ProfileTabs from '../components/profile/ProfileTabs';
import PostsTab from '../components/profile/tabs/PostsTab';
import ReviewsTab from '../components/profile/tabs/ReviewsTab';
import AboutTab from '../components/profile/tabs/AboutTab';
import CourseViewer from '../components/profile/tabs/CourseViewer';
import SubscriptionModal from '../components/profile/SubscriptionModal';
import './styles/ProfilePage.css';
import { getTrainerById } from '../../api/TrainerAPI';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ServicesTab from '../components/profile/tabs/ServicesTab';
import JoinNewroCTA from '../components/JoinNewroCTA';
import { getServiceTier } from '../utils/tierHelper';

const ProfilePage = () => {
    const { personId } = useParams();
    const navigate = useNavigate();
    const [person, setPerson] = useState(null);
    const [posts, setposts] = useState([]);
    const [reviews, setreviews] = useState([]);
    const [activeTab, setActiveTab] = useState('about');
    const [showSubscribeModal, setShowSubscribeModal] = useState(false);
    const [selectedService, setSelectedService] = useState(null);
    const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
    const [currentUserId, setCurrentUserId] = useState(null);

    // Check if current user owns this profile
    const isOwner = currentUserId && currentUserId === personId;

    // Listen for auth state changes
    useEffect(() => {
        const auth = getAuth();
        const unsubscribe = onAuthStateChanged(auth, (user) => {
            setCurrentUserId(user?.uid || null);
        });
        return () => unsubscribe();
    }, []);

    useEffect(() => {
        getTrainerData(personId);
    }, [personId, navigate]);

    useEffect(() => {
        const handleResize = () => {
            const isMobile = window.innerWidth <= 768;
            setIsMobile(isMobile);

            if (!isMobile && activeTab === 'services') {
                setActiveTab('about');
            }
        };

        window.addEventListener("resize", handleResize);
        return () => window.removeEventListener("resize", handleResize);
    }, [activeTab]);

    async function getTrainerData(id) {
        const res = await getTrainerById(id);
        if (res.success) {
            setPerson(res.userData);
            setposts(res.posts);
            setreviews(res.reviews);
        } else {
            navigate('/connect');
        }
    }

    const handleSubscribe = (service = null) => {
        setSelectedService(service);
        setShowSubscribeModal(true);
    };

    const handleTabChange = (tab) => {
        setActiveTab(tab);
    };

    if (!person) {
        return (
            <div className="profile-loading">
                <div className="profile-loading__spinner"></div>
            </div>
        );
    }

    const renderTabContent = () => {
        switch (activeTab) {
            case 'about':
                return <AboutTab person={person} />;
            case 'posts':
                return <PostsTab posts={posts} />;
            case 'reviews':
                return <ReviewsTab reviews={reviews} rating={5} reviewCount={reviews.length} />;
            case 'services':
                return <ServicesTab person={person} handleSubscribe={handleSubscribe} />;
            case 'courses':
                return <CourseViewer trainerId={personId} />;
            default:
                return <AboutTab person={person} />;
        }
    };

    return (
        <div className="profile-page">
            <div className="profile-container">
                <div className="profile-header-wrapper">
                    <button className="profile-back-btn" onClick={() => navigate('/connect')}>
                        <ArrowBackIcon />
                    </button>
                    <ProfileHeader
                        person={person}
                        onSubscribe={handleSubscribe}
                    />
                </div>

                <div className="profile-content">
                    <div className="profile-main">
                        <ProfileTabs
                            isConsole={false}
                            activeTab={activeTab}
                            onTabChange={handleTabChange}
                            isMobile={isMobile}
                            showCourses={isOwner}
                        />
                        <div className="profile-tab-content">
                            {renderTabContent()}
                        </div>
                    </div>

                    <aside className="profile-sidebar">
                        <div className="profile-services-card">
                            <h3>Services</h3>
                            <div className="profile-services-list">
                                {person.services?.map(service => {
                                    const tier = getServiceTier(service.price);
                                    const TierIcon = tier.icon;
                                    return (
                                        <div key={service.id} className="profile-service-item" onClick={() => handleSubscribe(service)}>
                                            <div className="profile-service-top">
                                                <div className="profile-service-info">
                                                    <span className="profile-service-name">{service.name}</span>
                                                    <span className="profile-service-desc">{service.description}</span>
                                                </div>
                                                <div className="profile-service-tier" style={{ color: tier.color }}>
                                                    <TierIcon style={{ fontSize: '1.5rem' }} />
                                                    {/* <span className="profile-service-tier-label">{tier.label}</span> */}
                                                </div>
                                            </div>
                                            <div className="profile-service-price-bottom">
                                                <span className="profile-service-amount">R {service.price}</span>
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        </div>

                        <div className="profile-credentials-card">
                            <h3>Credentials</h3>
                            <div className="profile-credentials-list">
                                {person.profile?.qualifications?.slice(0, 3).map((qual, index) => (
                                    <div key={index} className="profile-credential-item">
                                        <span className="profile-credential-title">{qual.title}</span>
                                        <span className="profile-credential-meta">{qual.institution} • {qual.year}</span>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </aside>
                </div>
                <JoinNewroCTA variant="compact" />

            </div>

            {showSubscribeModal && (
                <SubscriptionModal
                    person={person}
                    selectedService={selectedService}
                    onClose={() => setShowSubscribeModal(false)}
                />
            )}
        </div>
    );
};

export default ProfilePage;
